import { hexToRgb, getLuminance, safe } from '../utils/contrast.js';
import { getIcon } from '../utils/icons-library.js';

// ════════════════════════════════════════════════════════════════════════
// JOURNEY (Track B · Ola 1.3) — Sección "trazo" / recorrido SVG scroll-draw.
// El path SVG se dibuja conforme el usuario hace scroll (motor en
// js/animations/journey-scroll.js). Los colores SIEMPRE vienen de la paleta
// (p.bgColor/textColor/accentColor → --bg/--text/--accent); nada hardcodeado.
//
// 3 variantes (mezclas de los 3 layouts validados):
//   v1 "Sendero"  → espina serpenteante vertical + tarjetas alternas (A×B)
//   v2 "Ruta Fija"→ sección fija (pinned) + trazo horizontal izq→der (C×B)
//   v3 "Ascenso"  → espina vertical recta + hitos centrados apilados (A×C)
// ════════════════════════════════════════════════════════════════════════

// SVG inline (sin emojis) para el botón de cambiar icono del item
const PALETTE_SVG = '<svg viewBox="0 0 24 24" width="15" height="15" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><circle cx="13.5" cy="6.5" r=".5"/><circle cx="17.5" cy="10.5" r=".5"/><circle cx="8.5" cy="7.5" r=".5"/><circle cx="6.5" cy="12.5" r=".5"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.504 5.555-5.555C21.965 6.012 17.461 2 12 2z"/></svg>';

// Campos de item alineados con el schema de la IA (llm_schemas.py → items: {name, desc, icon}),
// el mismo contrato que features/stats/process. Así la IA puede generar Y reescribir estos
// textos sin campos nuevos: 'name' = título corto del paso, 'desc' = explicación, 'icon' = Lucide.
const DEFAULT_ITEMS = [
    { name: 'Descúbrenos',          desc: 'Cuéntanos qué necesitas y exploramos juntos las opciones.',  icon: 'message-circle' },
    { name: 'Planificamos',         desc: 'Diseñamos una propuesta a tu medida, paso a paso.',           icon: 'pen-tool' },
    { name: 'Lo hacemos realidad',  desc: 'Ejecutamos con precisión y te mantenemos siempre informado.', icon: 'code' },
    { name: 'Resultado',            desc: 'Entregamos y te acompañamos para que todo funcione.',          icon: 'check-circle' },
];

/** Props por defecto al insertar desde el catálogo (Track B). `base` = colores del tema. */
export const journeyDefaults = (base = {}) => ({
    type: 'journey',
    props: {
        title: 'Cómo funciona',
        subtitle: 'Un recorrido claro, de principio a fin',
        variant: 'v1',
        bgColor: base.bgColor || '#0b0e14',
        textColor: base.textColor || '#e7e9f0',
        accentColor: base.accentColor || '#6366f1',
        items: JSON.parse(JSON.stringify(DEFAULT_ITEMS)),
    },
});

export const journeyBlock = (rawProps, index = 0) => {
    const p = safe(rawProps);
    const variant = p.variant || 'v1';
    const items = (p.items && p.items.length) ? p.items : DEFAULT_ITEMS;

    const bg     = p.bgColor || '#0b0e14';
    const text   = p.textColor || '#e7e9f0';
    const accent = p.accentColor || '#6366f1';
    const isDark = getLuminance(...hexToRgb(bg)) < 0.179;

    const btnBg   = isDark ? '#ffffff' : '#111827';
    const btnIcon = isDark ? '#111827' : '#ffffff';
    const gradId  = `journey-grad-${index}`;

    // --- Toolbar de edición por item (add / icono / mover / borrar) ---
    const itemToolbar = (i) => `
        ${i !== 0 ? `<button class="item-add-btn card-add-left" data-array="items" data-index="${i}">+</button>` : ''}
        <button class="item-add-btn card-add-right" data-array="items" data-index="${i}">+</button>
        <div class="item-tools">
            <button class="tool-btn icon-picker-btn" data-array="items" data-index="${i}" title="Cambiar icono" style="background:${btnBg} !important; color:${btnIcon} !important;">${(window.icons && window.icons.palette) || PALETTE_SVG}</button>
            <button class="tool-btn item-drag-handle" title="Mover" style="background:${btnBg} !important; color:${btnIcon} !important;">${window.icons ? window.icons.drag : ''}</button>
            <button class="tool-btn item-delete-btn" data-array="items" data-index="${i}" title="Borrar" style="background:${btnBg} !important; color:${btnIcon} !important;">${window.icons ? window.icons.trash : ''}</button>
        </div>`;

    // --- Encabezado compartido ---
    const header = `
        <div class="journey-head">
            ${p.title ? `<h2 data-field="title" contenteditable="true">${p.title}</h2>` : ''}
            ${p.subtitle ? `<p data-field="subtitle" contenteditable="true">${p.subtitle}</p>` : ''}
        </div>`;

    // --- SVG del trazo (el `d` lo genera el motor a partir de los puntos reales) ---
    // preserveAspectRatio="none": el motor fija viewBox = tamaño en px (escala 1:1).
    const trackSvg = `
        <svg class="journey-svg" preserveAspectRatio="none" aria-hidden="true" focusable="false">
            <defs>
                <linearGradient id="${gradId}" class="journey-grad" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0"  style="stop-color:var(--accent)" stop-opacity="1"></stop>
                    <stop offset="1"  style="stop-color:var(--accent)" stop-opacity="0.45"></stop>
                </linearGradient>
            </defs>
            <path class="journey-rail" d=""></path>
            <path class="journey-route" stroke="url(#${gradId})" d=""></path>
            <circle class="journey-tip" r="6" cx="-50" cy="-50"></circle>
        </svg>`;

    // --- Tarjeta de hito (editable) ---
    const stepCard = (item, i) => `
            <div class="journey-step sortable-item ${i % 2 === 0 ? 'is-left' : 'is-right'}" data-item-index="${i}" data-array-name="items" data-step="${i}">
                ${itemToolbar(i)}
                <div class="journey-dot"><span class="journey-num">${i + 1}</span></div>
                <div class="journey-card">
                    ${item.icon ? `<div class="journey-icon">${getIcon(item.icon, 22)}</div>` : ''}
                    <h3 data-field="name" contenteditable="true">${item.name || ''}</h3>
                    <p data-field="desc" contenteditable="true">${item.desc || ''}</p>
                </div>
            </div>`;

    const stepsHtml = items.map((item, i) => stepCard(item, i)).join('');

    const sectionStyle = `--bg:${bg}; --text:${text}; --accent:${accent}; --font:'${p.fontFamily || 'Inter'}';`;
    const baseClass = `reveal variant-${variant} journey-layout journey-${variant}`;

    // ═══════════════════════════════════════════
    // v2 — Ruta Fija (pinned): la sección se fija y el trazo se dibuja horizontal
    // ═══════════════════════════════════════════
    if (variant === 'v2') {
        return `
    <section id="bloque-journey-${index}" class="${baseClass} journey-pinned" data-journey-variant="v2" style="${sectionStyle}">
        <div class="journey-pin">
            ${header}
            <div class="journey-track" data-journey-track>
                ${trackSvg}
                <div class="journey-steps">
                    ${stepsHtml}
                </div>
            </div>
        </div>
    </section>`;
    }

    // ═══════════════════════════════════════════
    // v1 "Sendero" (serpenteante) y v3 "Ascenso" (recta) — comparten estructura
    // vertical; el motor decide la curvatura según data-journey-variant.
    // ═══════════════════════════════════════════
    return `
    <section id="bloque-journey-${index}" class="${baseClass}" data-journey-variant="${variant}" style="${sectionStyle}">
        ${header}
        <div class="journey-track" data-journey-track>
            ${trackSvg}
            <div class="journey-steps">
                ${stepsHtml}
            </div>
        </div>
    </section>`;
};
